import { event, isInitialized } from '@fullstory/browser';

import { type HubActionsId } from '~/modules/hub/types/hub.type';
import { type PlanCode } from '~/modules/onboarding/types/onboarding.type';
import { type PickListConfigForm } from '~/modules/picklist/types/picklist.type';
import { type PrintAssetType } from '~/modules/print/types/print.type';
import { type Architecture, type OS } from '~/types/Device';
import { type StoreName } from '~/types/Store';
import { type TrackingAppLocation } from '~/utils/TrackingHelper/types';
import { isDebuggingAnalytics } from '~/utils/browser-tools';
import { getJWTInfoFromCookie } from '~/utils/jwt';

export enum FullStoryEvent {
  'CARRIER_ADDRESS_VALIDATION_BYPASS' = 'Carrier Address Validation Bypassed',
  'CLICK_TO_PRINT_ENGAGEMENT' = 'Click-to-print Engagement',
  'CONFIRMATION_DRAWER_OPENED' = 'Confirmation Drawer Opened',
  'CSV_IMPORT_FLOW_EVENT' = 'CSV Import Flow Event',
  'CSV_WELCOME_DIALOG_ACTION' = 'CSV Welcome Dialog Action',
  'CSV_WELCOME_DIALOG_SHOWN' = 'CSV Welcome Dialog Shown',
  'FAILED_INVOICE_PAYMENT_ALERT_SHOWN' = 'Failed Invoice Payment Alert Shown',
  'HUB_EVENT' = 'Hub Event',
  'INCENTIVE_TRACKER_EVENT' = 'Incentive Tracker Event',
  'INVALID_ADDRESS_PHONE_NUMBER_SHOWN' = 'Invalid Phone Number Shown',
  'INVALID_IMPORTED_ADDRESS_ALERT_SHOWN' = 'Invalid Imported Address Alert Shown',
  'INVALID_IMPORTED_ADDRESS_MODAL_SHOWN' = 'Invalid Imported Address Modal Shown',
  'INVALID_PAYMENT_METHOD_ALERT_SHOWN' = 'Invalid Payment Method Alert Shown',
  'LABEL_PURCHASE_DRAWER_OPENED' = 'Label Purchase Drawer Opened',
  'LABEL_PURCHASE_DRAWER_ORDER_NAVIGATION' = 'Label Purchase Drawer Order Navigation',
  'MANUAL_LABEL_WELCOME_DIALOG_ACTION' = 'Manual Label Welcome Dialog Action',
  'MANUAL_LABEL_WELCOME_DIALOG_SHOWN' = 'Manual Label Welcome Dialog Shown',
  'ONBOARDING_FLOW_EVENT' = 'Onboarding Flow Event',
  'PICKLIST_CONFIG_DRAWER_DISMISSED' = 'Picklist Config Drawer Dismissed',
  'PICKLIST_CONFIG_DRAWER_OPENED' = 'Picklist Config Drawer Opened',
  'PICKLIST_CONFIG_UPDATED' = 'Picklist Config Updated',
  'PICKLIST_GENERATED' = 'Picklist Generated',
  'PICKLIST_ITEM_LIMIT_REACHED' = 'Picklist Item Limit Reached',
  'PRINT_DOCUMENTS' = 'Print Documents',
  'PRINT_JOB_RESULT' = 'Print Job Result',
  'PRINTER_SELECTED_FOR_ASSET' = 'Printer Selected for Asset',
  'PRO_PLAN_GATE_CTA_CLICKED' = 'Pro Plan Gate CTA Clicked',
  'RETURN_DRAWER_OPENED' = 'Return Drawer Opened',
  'SCAN_ACTION_DISPATCHED' = 'Scan Action Dispatched',
  'SCAN_ACTION_RESULT' = 'Scan Action Result',
  'SCAN_SUPPRESSED' = 'Scan Suppressed',
  'SENDER_FILTER_APPLIED' = 'Sender Filter Applied',
  'SHIPMENT_DRAWER_ORDER_NAVIGATION' = 'Shipment Drawer Order Navigation',
  'SIGNED_UP' = 'Signed Up',
  'STORE_CONNECTED' = 'Store Connected',
  'STORE_CONNECTION_WELCOME_MODAL_ACTION' = 'Store Connection Welcome Modal Action',
  'STORE_CONNECTION_WELCOME_MODAL_SHOWN' = 'Store Connection Welcome Modal Shown',
  'STORE_SYNC_COMPLETE' = 'Store Sync Completed',
  'STORE_SYNC_FAILED' = 'Store Sync Failed',
  'STORE_SYNC_STARTED' = 'Store Sync Started',
  'USER_PLAN_UPGRADED' = 'User Plan Upgraded',
}

export type FullStoryEventProperties = {
  [FullStoryEvent.CARRIER_ADDRESS_VALIDATION_BYPASS]: Record<string, never>;
  [FullStoryEvent.CLICK_TO_PRINT_ENGAGEMENT]: {
    action: 'buy' | 'download_click' | 'learn_more_click' | 'post_print_action_taken';
    arch?: Architecture;
    os?: OS;
    post_print_action?: 'download_docs' | 'other' | 'send_tracking_email';
    source?: 'bulk_post_purchase_right_rail' | 'orders_right_rail';
  };
  [FullStoryEvent.CONFIRMATION_DRAWER_OPENED]: {
    is_bulk: boolean;
    order_ids: string;
  };
  [FullStoryEvent.CSV_IMPORT_FLOW_EVENT]:
    | { action: 'go_to_orders' | 'upload_another'; type: 'post_import_action' }
    | { type: 'address_panel_completed' }
    | { type: 'csv_import' }
    | { type: 'file_selected' }
    | { type: 'template_applied' }
    | { type: 'template_created' }
    | { type: 'template_deleted' };
  [FullStoryEvent.CSV_WELCOME_DIALOG_ACTION]: {
    action: 'dismiss' | 'upload';
  };
  [FullStoryEvent.CSV_WELCOME_DIALOG_SHOWN]: Record<string, never>;
  [FullStoryEvent.FAILED_INVOICE_PAYMENT_ALERT_SHOWN]: Record<string, never>;
  [FullStoryEvent.HUB_EVENT]:
    | {
        action:
          | 'check_rates'
          | 'connect_store'
          | 'create_label'
          | 'shipment_alerts_get_notified'
          | 'shipment_alerts_view_plans'
          | 'upload_csv'
          | HubActionsId;
        isOver30Days?: true;
        type: 'hub_action';
      }
    | {
        action: 'blog' | 'click_to_print' | 'help_center' | 'referral' | 'scan_to_print';
        isOver30Days: true;
        type: 'hub_resource';
      };
  [FullStoryEvent.INCENTIVE_TRACKER_EVENT]: {
    action: 'confirmed' | 'cta_clicked' | 'opened';
    promotionId: number;
  };
  [FullStoryEvent.INVALID_ADDRESS_PHONE_NUMBER_SHOWN]: Record<string, never>;
  [FullStoryEvent.INVALID_IMPORTED_ADDRESS_ALERT_SHOWN]: {
    location: TrackingAppLocation;
  };
  [FullStoryEvent.INVALID_IMPORTED_ADDRESS_MODAL_SHOWN]: Record<string, never>;
  [FullStoryEvent.INVALID_PAYMENT_METHOD_ALERT_SHOWN]: Record<string, never>;
  [FullStoryEvent.LABEL_PURCHASE_DRAWER_OPENED]: {
    is_bulk: boolean;
    order_ids: string;
  };
  [FullStoryEvent.LABEL_PURCHASE_DRAWER_ORDER_NAVIGATION]: {
    current_order_index: number;
    direction: 'next' | 'previous';
    order_id: string;
    order_shipment_id?: number;
    target_order_index: number;
    total_orders: number;
  };
  [FullStoryEvent.MANUAL_LABEL_WELCOME_DIALOG_ACTION]: {
    action: 'create' | 'dismiss';
  };
  [FullStoryEvent.MANUAL_LABEL_WELCOME_DIALOG_SHOWN]: Record<string, never>;
  [FullStoryEvent.ONBOARDING_FLOW_EVENT]:
    | {
        choice: 'check_rates' | 'connect_store' | 'developer' | 'explore' | 'no_store';
        type: 'decision_made';
      }
    | { action: 'dismissed' | 'upgraded' | 'viewed'; type: 'pro_pitch_modal' }
    | { cause: 'price_change' | 'rate_unavailable'; type: 'rate_updated' }
    | { rate_count: number; step: 'check_rates' | 'purchase'; type: 'rates_fetched' }
    | { service_level_token: string; step: 'check_rates' | 'purchase'; type: 'rate_selected' }
    | { service_level_token: string; step: 'check_rates'; type: 'rate_action_clicked' }
    | { store_platform: StoreName; type: 'store_connection_skipped' }
    | { type: 'purchase_exited' }
    | { type: 'purchase_saved' }
    | { type: 'purchased' };
  [FullStoryEvent.PICKLIST_CONFIG_DRAWER_DISMISSED]: {
    source: 'orders_toolbar';
  };
  [FullStoryEvent.PICKLIST_CONFIG_DRAWER_OPENED]: {
    source: 'orders_toolbar';
  };
  [FullStoryEvent.PICKLIST_CONFIG_UPDATED]: {
    is_updated: boolean;
    source: 'orders_toolbar' | 'settings';
    values: PickListConfigForm;
  };
  [FullStoryEvent.PICKLIST_GENERATED]: { config: Record<string, unknown> | null };
  [FullStoryEvent.PICKLIST_ITEM_LIMIT_REACHED]: {
    items_count: number;
    source: 'orders_toolbar';
  };
  [FullStoryEvent.PRINT_DOCUMENTS]: {
    action?: 'buy_and_print';
    asset?: PrintAssetType | string;
    correlation_id?: string;
    document_count?: number;
    entrypoint?: string;
    order_channel?: string;
    printer_name?: string;
    source?: 'label_purchase_drawer';
  };
  /**
   * Final QZ Tray print outcome, mirrored from `device.print.completed` /
   * `device.print.failed` (FE-10.2). `result` discriminates success from failure;
   * remaining fields mirror the `device.print.*` payload.
   */
  [FullStoryEvent.PRINT_JOB_RESULT]: {
    asset?: string;
    correlation_id?: string;
    document_count?: number;
    entrypoint?: string;
    error_code?: string;
    error_message?: string;
    order_channel?: string;
    print_job_status_code?: string;
    printer_name?: string;
    result: 'failure' | 'success';
    status?: string;
  };
  [FullStoryEvent.PRINTER_SELECTED_FOR_ASSET]: {
    asset: PrintAssetType;
    os: OS;
    printer_name: string;
  };
  [FullStoryEvent.PRO_PLAN_GATE_CTA_CLICKED]: { source: string };
  [FullStoryEvent.RETURN_DRAWER_OPENED]: {
    is_bulk: boolean;
    order_ids: string;
  };
  /**
   * Scan Action lifecycle (QPACK-658), mirrored from `device.scan.completed` /
   * `device.scan.failed`. `scan_action` and `error_code` carry the same values as
   * `event_action` and `error_code` on the device event, `barcode_resolved` is set exactly
   * when the device event's `barcode_content.order_object_id` is, and `correlation_id`
   * joins both.
   */
  [FullStoryEvent.SCAN_ACTION_DISPATCHED]: {
    correlation_id: string;
    scan_action: string;
  };
  [FullStoryEvent.SCAN_ACTION_RESULT]: {
    barcode_resolved: boolean;
    correlation_id: string;
    error_code?: string;
    result: 'failure' | 'success';
    scan_action?: string;
  };
  [FullStoryEvent.SCAN_SUPPRESSED]: {
    correlation_id: string;
    reason: 'rejected_duplicate' | 'rejected_overflow';
  };
  [FullStoryEvent.SENDER_FILTER_APPLIED]: {
    selected_sender_address_count: number;
  };
  [FullStoryEvent.SHIPMENT_DRAWER_ORDER_NAVIGATION]: {
    current_order_index: number;
    direction: 'next' | 'previous';
    order_id: string;
    order_shipment_id?: number;
    target_order_index: number;
    total_orders: number;
  };
  [FullStoryEvent.SIGNED_UP]: { type: 'email' | 'sso' };
  [FullStoryEvent.STORE_CONNECTED]: { days_since_joined: number; store_name: StoreName };
  [FullStoryEvent.STORE_CONNECTION_WELCOME_MODAL_ACTION]: {
    action: 'dismiss' | 'done' | 'setup';
  };

  [FullStoryEvent.STORE_CONNECTION_WELCOME_MODAL_SHOWN]: Record<string, never>;
  [FullStoryEvent.STORE_SYNC_COMPLETE]: { status: 'DONE' | 'NO_DATA' };
  [FullStoryEvent.STORE_SYNC_FAILED]: Record<string, never>;

  [FullStoryEvent.STORE_SYNC_STARTED]: Record<string, never>;

  [FullStoryEvent.USER_PLAN_UPGRADED]: { plan: PlanCode };
};

const getStandardProperties = () => {
  const jwtInfo = getJWTInfoFromCookie();
  return {
    login_user_id: jwtInfo?.login_user_id,
  };
};

/**
 * Send a custom tracking event to Full Story
 * @param name
 * @param properties
 */
export const fullStoryTracking = <E extends keyof FullStoryEventProperties>(
  name: E,
  properties: FullStoryEventProperties[E],
) => {
  const eventProperties = { ...properties, ...getStandardProperties() } as Record<string, unknown>;
  // eslint-disable-next-line no-console
  if (isDebuggingAnalytics()) console.log(`%c⭕ ${name}`, 'font-weight:bold;', eventProperties);
  if (isInitialized()) event(name, eventProperties);
};
